import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";
import {
  buildActionColumn,
  buildAvatarDetailsColumn,
  buildExpandableCountColumn,
  buildExpandableCountMultiColumn,
  buildTagColumn
} from "@/components/Table";
import Tag from "@/components/Tag";
import { toFormat } from "@/lib/dateHelpers";
import { DateTime } from "luxon";
import { formatPosition } from "../committeePositions";
import type {
  CommitteeParticipantNode,
  CommitteeParticipantWithCommittee,
  CommitteeRow,
  CommitteeStatus,
  CommitteeTermNode
} from "../types";

const STATUS_LABELS: Record<CommitteeStatus, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive"
};

const MOBILE_ONLY_HIDDEN_CLASS = "max-lg:hidden";

function mobileDetailField(label: string, value: ReactNode) {
  return (
    <div className="text-body-small text-sys-brand-on-surface-variant">
      <span className="font-medium mr-1 whitespace-nowrap">{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function statusColumn<T extends { status: CommitteeStatus }>(): ColumnDef<T, unknown> {
  return buildTagColumn<T>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => STATUS_LABELS[row.status],
    getTagType: (row) => (row.status === "ACTIVE" ? "success" : "surface"),
    getTagStyle: () => "strong",
    enableSorting: true,
    sortType: "text",
    size: 120
  });
}

const committeeNameColumn: ColumnDef<CommitteeRow, unknown> = {
  accessorKey: "committeeName",
  header: "Committee Name",
  size: 280,
  meta: {
    sortType: "text",
    headerClassName: "hide-header-in-column"
  }
};

function participantStatus(participant: CommitteeParticipantNode): CommitteeStatus {
  const today = DateTime.now().startOf("day");
  const startDate = DateTime.fromISO(participant.startDate).startOf("day");
  const endDate = participant.endDate ? DateTime.fromISO(participant.endDate).startOf("day") : null;

  return startDate <= today && (!endDate || endDate >= today) ? "ACTIVE" : "INACTIVE";
}

function participantName(participant: CommitteeParticipantWithCommittee) {
  return participant.person?.displayName ?? "Unknown Participant";
}

function participantPgaId(participant: CommitteeParticipantWithCommittee) {
  return participant.person?.pgaId ?? participant.pgaId;
}

export function canViewCommitteeParticipant(participant: CommitteeParticipantNode): boolean {
  return participant.person != null;
}

function participantMobileDetailFields(participant: CommitteeParticipantWithCommittee) {
  const status = participantStatus(participant);

  return (
    <div className="flex w-full flex-col items-start gap-1">
      {mobileDetailField("Position", formatPosition(participant.position))}
      {mobileDetailField("Term Start Date", toFormat(participant.startDate))}
      {mobileDetailField("Term End Date", participant.endDate ? toFormat(participant.endDate) : "--")}
      <div className="w-fit">
        <Tag tagType={status === "ACTIVE" ? "success" : "surface"} size="small" tagStyle="strong">
          {STATUS_LABELS[status]}
        </Tag>
      </div>
    </div>
  );
}

function participantDetailsColumns(isCompactLayout: boolean): ColumnDef<CommitteeParticipantWithCommittee, unknown>[] {
  return buildAvatarDetailsColumn({
    isCompactLayout,
    getTitle: participantName,
    getSubtitle: (row) => participantPgaId(row),
    subtitleLabel: "PGA ID",
    getPicture: (row) => row.person?.profilePhoto ?? undefined,
    header: "Participant",
    sortType: "text",
    additionalMobileFields: isCompactLayout ? participantMobileDetailFields : undefined,
    id: "participant"
  });
}

export function getTermColumns(): ColumnDef<CommitteeRow, unknown>[] {
  return [
    committeeNameColumn,
    {
      ...buildExpandableCountColumn<CommitteeRow>({
        accessorKey: "participants",
        header: "Participants",
        getCount: (row) => row.participants ?? 0,
        size: 140
      }),
      enableSorting: true,
      meta: {
        headerClassName: "max-sm:inline",
        sortType: "count"
      }
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: ({ row }) => (row.original.startDate ? toFormat(row.original.startDate) : "--"),
      size: 140,
      meta: { sortType: "date" }
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: ({ row }) => (row.original.endDate ? toFormat(row.original.endDate) : "--"),
      size: 140,
      meta: { sortType: "date" }
    },
    statusColumn<CommitteeRow>()
  ];
}

export function getCommitteeTermDetailsColumns(): ColumnDef<CommitteeTermNode, unknown>[] {
  return [
    {
      accessorKey: "startDate",
      header: "Term Start Date",
      cell: ({ row }) => toFormat(row.original.startDate),
      meta: { sortType: "date" }
    },
    {
      accessorKey: "endDate",
      header: "Term End Date",
      cell: ({ row }) => (row.original.endDate ? toFormat(row.original.endDate) : "--"),
      meta: { sortType: "date" }
    },
    {
      accessorKey: "participantCount",
      header: "Participants",
      meta: { sortType: "count" }
    },
    {
      ...buildTagColumn<CommitteeTermNode>({
        accessorKey: "status",
        header: "Status",
        getTagLabel: (row) => STATUS_LABELS[row.status],
        getTagType: (row) => (row.status === "ACTIVE" ? "success" : "surface"),
        getTagStyle: () => "strong",
        enableSorting: true,
        sortType: "text"
      }),
      accessorFn: (row) => STATUS_LABELS[row.status]
    }
  ];
}

export function getAllColumns({
  expanded,
  onExpand
}: {
  expanded: Record<string, string | null>;
  onExpand: (rowId: string, panelId: string | null) => void;
}): ColumnDef<CommitteeRow, unknown>[] {
  return [
    committeeNameColumn,
    {
      ...buildExpandableCountMultiColumn<CommitteeRow>({
        panelId: "terms",
        accessorKey: "historicalTerms",
        header: "Historical Terms",
        getCount: (row) => row.historicalTerms ?? 0,
        expanded,
        onExpand,
        size: 160,
        minSize: 160
      }),
      enableSorting: true,
      meta: {
        headerClassName: "max-sm:inline",
        sortType: "count"
      }
    },
    {
      ...buildExpandableCountMultiColumn<CommitteeRow>({
        panelId: "participants",
        accessorKey: "historicalParticipations",
        header: "Historical Participations",
        getCount: (row) => row.historicalParticipations ?? 0,
        expanded,
        onExpand,
        size: 180
      }),
      enableSorting: true,
      meta: {
        headerClassName: "max-sm:inline",
        sortType: "count"
      }
    },
    {
      accessorKey: "latestEndDate",
      header: "Latest End Date",
      cell: ({ row }) => (row.original.latestEndDate ? toFormat(row.original.latestEndDate) : "--"),
      size: 160,
      meta: { sortType: "date" }
    },
    statusColumn<CommitteeRow>()
  ];
}

export function getCommitteeParticipantDetailsColumns(
  isCompactLayout: boolean,
  onViewParticipant: (participant: CommitteeParticipantWithCommittee) => void
): ColumnDef<CommitteeParticipantWithCommittee, unknown>[] {
  const compactOnlyHiddenClass = isCompactLayout ? MOBILE_ONLY_HIDDEN_CLASS : undefined;

  return [
    ...participantDetailsColumns(isCompactLayout),
    {
      accessorKey: "position",
      header: "Position",
      cell: ({ row }) => formatPosition(row.original.position),
      meta: { sortType: "text", className: compactOnlyHiddenClass }
    },
    {
      accessorKey: "startDate",
      header: "Term Start Date",
      cell: ({ row }) => toFormat(row.original.startDate),
      meta: { sortType: "date", className: compactOnlyHiddenClass }
    },
    {
      accessorKey: "endDate",
      header: "Term End Date",
      cell: ({ row }) => (row.original.endDate ? toFormat(row.original.endDate) : "--"),
      meta: { sortType: "date", className: compactOnlyHiddenClass }
    },
    {
      ...buildTagColumn<CommitteeParticipantWithCommittee>({
        accessorKey: "status",
        header: "Status",
        getTagLabel: (row) => STATUS_LABELS[participantStatus(row)],
        getTagType: (row) => (participantStatus(row) === "ACTIVE" ? "success" : "surface"),
        getTagStyle: () => "strong",
        enableSorting: true,
        sortType: "text",
        meta: { className: compactOnlyHiddenClass }
      }),
      accessorFn: (row) => STATUS_LABELS[participantStatus(row)]
    },
    buildActionColumn<CommitteeParticipantWithCommittee>({
      onViewClick: onViewParticipant,
      showViewButton: canViewCommitteeParticipant,
      isRowNavigable: canViewCommitteeParticipant,
      viewButtonTitle: "View participant details",
      actionId: (rowId) => `committee-participant-action-${rowId}`,
      setMenu: () => undefined
    })
  ];
}
